'use client'

import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { guestLoginService } from '~/services/domain/auth/LoginService'

/** `loggedIn`: sessão aberta; `registered`: o e-mail já tem conta e a tela pede a senha (useLogin); `failed`: erro. */
export type IGuestLoginOutcome = 'loggedIn' | 'registered' | 'failed'

const ALREADY_REGISTERED = 'Usuário já cadastrado'

/**
 * E-mail do carrinho para quem não está logado (`custom-login`): cria o cliente temporário e segue para o checkout. Se
 * o e-mail já tem conta, avisa e devolve `registered`; a tela mostra a senha e entra pelo `useLogin` com `/checkout`.
 */
export function useGuestLogin() {
    const router = useRouter()

    const guestMutation = useMutation({
        mutationFn: (email: string) => guestLoginService(email),
        onSuccess: (result) => {
            // E-mail novo: o checkout abre o cadastro em seguida, então não há login a anunciar.
            if (result.status === 'success') {
                router.push('/checkout')
            } else if (result.message === ALREADY_REGISTERED) {
                toast.info('Usuário já cadastrado!', { autoClose: 2000 })
            } else {
                toast.error(result.message, { autoClose: 2000 })
            }
        },
    })

    const submitEmail = useCallback(
        async (email: string): Promise<IGuestLoginOutcome> => {
            const result = await guestMutation.mutateAsync(email)
            if (result.status === 'success') return 'loggedIn'
            return result.message === ALREADY_REGISTERED ? 'registered' : 'failed'
        },
        [guestMutation.mutateAsync],
    )

    return { submitEmail, isLoading: guestMutation.isPending }
}
